// Extracted from src/routes/en.services.$service.$location.tsx to keep the
// full service catalog out of the eager route bundle.
import { Link } from "@tanstack/react-router";
import {
  Phone,
  MessageCircle,
  Mail,
  CheckCircle2,
  Clock,
  Globe2,
  MapPin,
  Star,
  ArrowRight,
} from "lucide-react";
import { SERVICES, type ServiceId } from "@/data/services";
import { getLocation, locations } from "@/data/locations";
import { RelatedComparisons, categoryForService } from "@/components/RelatedComparisons";
import {
  RelatedServicesInLocation,
  type ServiceKind,
} from "@/components/RelatedServicesInLocation";
import { EntityPanel } from "@/components/EntityPanel";
import { cityEn, serviceKeywordEn, regionEn } from "@/lib/seo-keywords";

export function ComboServicePageEn({
  service,
  locationSlug,
}: {
  service: ServiceId;
  locationSlug: string;
}) {
  const s = SERVICES[service];
  const loc = getLocation(locationSlug)!;
  const others = Object.values(SERVICES)
    .filter((x) => x.id !== s.id)
    .slice(0, 6);
  const sameRegion = locations
    .filter((l) => l.region === loc.region && l.slug !== loc.slug)
    .slice(0, 8);

  return (
    <main className="min-h-screen bg-navy-deep text-white">
      {/* Hero */}
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] to-[#0d2150] px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <nav className="flex flex-wrap items-center gap-2 text-xs">
            <Link to="/en" className="text-amber-300 hover:underline">
              EN home
            </Link>
            <span className="text-white/30">/</span>
            <Link to="/en/services" className="text-amber-300 hover:underline">
              Services
            </Link>
            <span className="text-white/30">/</span>
            <Link
              to="/en/services/$service"
              params={{ service: s.slug }}
              className="text-amber-300 hover:underline"
            >
              {s.nameEn}
            </Link>
            <span className="text-white/30">/</span>
            <span className="text-white/80">{loc.nameEn}</span>
            <span className="text-white/30">·</span>
            <Link
              to="/services/$service/$location"
              params={{ service: s.slug, location: loc.slug }}
              className="text-white/80 hover:text-amber-300"
            >
              ภาษาไทย
            </Link>
          </nav>

          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-600/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-300">
            <MapPin className="h-3 w-3" /> Near you · {loc.nameEn}
          </div>

          <h1 className="mt-3 text-3xl font-extrabold leading-tight sm:text-5xl">
            {serviceKeywordEn(s.id, s.nameEn)} in {cityEn(loc)}
            <span className="block text-base font-semibold text-white/60 sm:text-lg">
              {loc.nameEn === cityEn(loc) ? regionEn(loc.region) : `${loc.nameEn} · ${regionEn(loc.region)}`}
            </span>
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/80 sm:text-lg">
            Looking for {serviceKeywordEn(s.id, s.nameEn).toLowerCase()} in {cityEn(loc)}?{" "}
            {s.taglineEn} NYC Legal &amp; Notary Service covers {loc.nameEn} with same-day pickup,
            certified translation and worldwide DHL/FedEx delivery.
          </p>

          <div className="mt-6 flex flex-wrap gap-3 text-xs">
            <span className="rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 font-semibold text-amber-200">
              From ฿{s.priceFrom.toLocaleString()}
            </span>
            <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-white/80">
              <Clock className="-mt-0.5 mr-1 inline h-3 w-3" /> {s.turnaround}
            </span>
            <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-white/80">
              <Globe2 className="-mt-0.5 mr-1 inline h-3 w-3" /> {s.languagesCount}+ languages
            </span>
            <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-amber-200">
              <Star className="-mt-0.5 mr-1 inline h-3 w-3" /> 6 notarial attorneys
            </span>
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href="tel:+66832494999"
              className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep shadow-xl shadow-amber-500/30 hover:bg-amber-400"
            >
              <Phone className="h-4 w-4" /> Call +66 83 249 4999
            </a>
            <a
              href="https://line.me/ti/p/@nycli"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-[#04A03A] px-5 py-3 text-sm font-bold text-white hover:bg-[#05B14C]"
            >
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a
              href="mailto:contact@nyclegal.co.th"
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white backdrop-blur hover:bg-white/10"
            >
              <Mail className="h-4 w-4" /> Free quote
            </a>
          </div>
        </div>
      </section>

      {/* Quick Answer Box (AEO) */}
      <section className="px-4 py-10">
        <div className="mx-auto max-w-5xl rounded-2xl border border-amber-400/30 bg-amber-500/5 p-6">
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-300">
            Quick answer · AI Overview
          </div>
          <h2 className="mt-2 text-lg font-bold sm:text-xl">
            How do I get {serviceKeywordEn(s.id, s.nameEn).toLowerCase()} in {cityEn(loc)}?
          </h2>
          <p className="mt-2 aeo-speakable text-sm text-white/85 sm:text-base">
            Send your documents via LINE @nycli or email. NYC Legal &amp; Notary Service quotes
            within 15 minutes, completes the {serviceKeywordEn(s.id, s.nameEn).toLowerCase()} in{" "}
            {s.turnaround.toLowerCase()}, and delivers to {loc.nameEn} ({cityEn(loc)}) by motorcycle
            (same day) or DHL/FedEx worldwide. Pricing starts from ฿{s.priceFrom.toLocaleString()}.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="px-4 py-8">
        <div className="mx-auto grid max-w-5xl grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { k: `${s.languagesCount}+`, l: "Languages in-house" },
            { k: "15 min", l: "Quote reply" },
            { k: "50+", l: "languages" },
            { k: "Same-day", l: `Pickup in ${loc.nameEn}` },
          ].map((x) => (
            <div
              key={x.l}
              className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-center"
            >
              <div className="text-2xl font-extrabold text-amber-300">{x.k}</div>
              <div className="mt-1 text-[11px] text-white/80">{x.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Quotable AEO callout — AI Overview / Copilot lift target */}
      <section className="px-4 py-8">
        <div className="mx-auto max-w-5xl rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-400/[0.12] via-amber-400/[0.04] to-transparent p-6 sm:p-8">
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-300">
            Quotable · AI Overview ready
          </div>
          <blockquote className="aeo-speakable mt-3 font-display text-base italic leading-relaxed text-white sm:text-lg">
            “NYC Legal &amp; Notary Service has provided {s.nameEn} to clients in {loc.nameEn},
            Thailand since 2016, registered with Thailand&rsquo;s MFA Department of Consular Affairs
            (Tax ID 0435567000061). Six in-house Notarial Services Attorneys, {s.languagesCount}+
            languages, quotes within 15 minutes, completion in {s.turnaround.toLowerCase()}, from ฿
            {s.priceFrom.toLocaleString()}, rated 4.9/5 across 4,250+ verified reviews.”
          </blockquote>
        </div>
      </section>

      {/* Documents */}
      <section className="px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold">
            Documents we handle for clients in {loc.nameEn}
          </h2>
          <ul className="mt-5 grid gap-2 sm:grid-cols-2">
            {s.documentsTh.slice(0, 14).map((d: string) => (
              <li
                key={d}
                className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white/85"
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-amber-300" /> {d}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Pricing */}
      {s.prices.length > 0 && (
        <section className="border-t border-white/10 bg-[#0d2150] px-4 py-10">
          <div className="mx-auto max-w-5xl">
            <h2 className="text-2xl font-extrabold">Transparent pricing</h2>
            <div className="mt-5 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
              <table className="w-full text-sm">
                <tbody>
                  {s.prices.map((p, i) => (
                    <tr key={p.item + i} className={i % 2 === 0 ? "bg-white/[0.02]" : ""}>
                      <td className="px-6 py-3 text-white/85">{p.item}</td>
                      <td className="px-4 py-3 text-right font-bold text-amber-300">{p.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-white/80">
              * Final price depends on volume, urgency and delivery distance within {loc.nameEn}.
              Free assessment via LINE @nycli.
            </p>
          </div>
        </section>
      )}

      {/* How To */}
      <section className="px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold">How it works for clients in {loc.nameEn}</h2>
          <ol className="mt-5 space-y-3">
            {s.howTo.map((step, i) => (
              <li
                key={step.title}
                className="flex gap-4 rounded-xl border border-white/10 bg-white/[0.04] p-4"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/20 text-sm font-extrabold text-amber-300">
                  {i + 1}
                </div>
                <div>
                  <div className="text-sm font-bold text-white">{step.title}</div>
                  <p className="mt-1 text-sm text-white/75">{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-white/10 px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold">
            FAQ — {s.nameEn} in {loc.nameEn}
          </h2>
          <div className="mt-5 space-y-3">
            {s.faqs.slice(0, 8).map((f) => (
              <details
                key={f.q}
                className="group rounded-xl border border-white/10 bg-white/[0.04] p-4"
              >
                <summary className="cursor-pointer text-sm font-bold text-white">{f.q}</summary>
                <p className="mt-2 text-sm text-white/75">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Other services in this location */}
      <section className="border-t border-white/10 px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold">Other services available in {loc.nameEn}</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((r) => (
              <Link
                key={r.id}
                to="/en/services/$service/$location"
                params={{ service: r.slug, location: loc.slug }}
                className="group rounded-2xl border border-white/10 bg-white/[0.04] p-4 transition hover:border-amber-400/40 hover:bg-white/[0.07]"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-300/80">
                  Related service
                </div>
                <div className="mt-1 text-sm font-bold text-white">{r.nameEn}</div>
                <div className="mt-1 line-clamp-2 text-xs text-white/80">{r.taglineEn}</div>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-amber-300 group-hover:gap-2">
                  {`Learn about ${r.nameEn}`} <ArrowRight className="h-3 w-3" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby locations */}
      {sameRegion.length > 0 && (
        <section className="border-t border-white/10 px-4 py-10">
          <div className="mx-auto max-w-5xl">
            <h2 className="text-2xl font-extrabold">
              {s.nameEn} in nearby areas ({regionEn(loc.region)})
            </h2>
            <ul className="mt-5 flex flex-wrap gap-2">
              {sameRegion.map((n) => (
                <li key={n.slug}>
                  <Link
                    to="/en/services/$service/$location"
                    params={{ service: s.slug, location: n.slug }}
                    className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/75 transition hover:border-amber-400/40 hover:text-amber-300"
                  >
                    <MapPin className="h-3 w-3" /> {n.nameEn}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="px-4 py-14">
        <div className="mx-auto max-w-3xl rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-8 text-center sm:p-12">
          <h2 className="text-2xl font-extrabold sm:text-3xl">
            Ready to start {s.nameEn} in {loc.nameEn}?
          </h2>
          <p className="mt-3 text-white/75">
            Free 15-minute quote · Same-day pickup in {loc.nameEn} · International courier delivery.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a
              href="tel:+66832494999"
              className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400"
            >
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
            <a
              href="mailto:contact@nyclegal.co.th"
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10"
            >
              <Mail className="h-4 w-4" /> contact@nyclegal.co.th
            </a>
          </div>
          <p className="mt-6 text-xs text-white/80">
            ภาษาไทย:{" "}
            <Link
              to="/services/$service/$location"
              params={{ service: s.slug, location: loc.slug }}
              className="underline hover:text-amber-300"
            >
              {s.nameTh}ใน{loc.nameTh}
            </Link>
          </p>
        </div>
      </section>
      <EntityPanel slug={loc.slug} locationName={loc.nameEn} locale="en" variant="dark" />
      <RelatedServicesInLocation
        currentService={(() => {
          const c = categoryForService(s.slug);
          return c === "translate" || c === "notary" || c === "apostille"
            ? (c as ServiceKind)
            : undefined;
        })()}
        locationSlug={locationSlug}
        locale="en"
      />
      {categoryForService(s.slug) && (
        <RelatedComparisons category={categoryForService(s.slug)!} locale="en" />
      )}
    </main>
  );
}
